import type { AvatarRendererPort } from '../ports';
import type { AvatarConnectionStatus } from '@shared/types';

/**
 * Default AvatarRendererPort implementation for this milestone. The avatar
 * system is out of scope, so this adapter deliberately does nothing real:
 * `connect()` resolves but leaves status at `not-connected`, rather than
 * faking a successful connection. This lets the Avatar UI be fully present
 * and interactive (per product requirement) while staying honest that no
 * renderer is actually attached yet.
 */
export class NotImplementedAvatarRenderer implements AvatarRendererPort {
  private status: AvatarConnectionStatus = 'not-connected';
  private listeners = new Set<(status: AvatarConnectionStatus) => void>();

  async connect(): Promise<void> {
    // Intentionally a no-op stand-in. Replace with a real adapter (e.g.
    // backed by window.synthCohost.avatarBridge) to enable real connections.
    this.setStatus('not-connected');
  }

  async disconnect(): Promise<void> {
    this.setStatus('not-connected');
  }

  getStatus(): AvatarConnectionStatus {
    return this.status;
  }

  onStatusChange(callback: (status: AvatarConnectionStatus) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private setStatus(status: AvatarConnectionStatus): void {
    this.status = status;
    this.listeners.forEach((listener) => listener(status));
  }
}
